import { z } from 'zod';
import {
  ConfidenceBand,
  ConfidenceMethod,
  Criticality,
  Disposition,
  Lifecycle,
  TechnicalHealth,
} from './vocabularies';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { MetricStatus, MetricValue, Money } from './metric';

/**
 * Estate read-models — the three analytical projections over the application
 * estate and the portfolio as a whole.
 *
 *   1. DuplicateReviewView      → "where do two applications do the same job?"
 *   2. TechnologyHeatmapView    → "what is old, what is fragile, what do we do?"
 *   3. PortfolioComparisonView  → "what actually changed between two pictures?"
 *
 * They are separated from `read-models.ts` because all three share one property
 * the eight original projections do not: **their central claim is inferred, not
 * recorded.** Nothing in a CMDB says "these two applications are duplicates" or
 * "this product was renamed rather than replaced"; a rule said so, over evidence
 * of varying quality, with a confidence.
 *
 * That is why every type here carries its reasoning alongside its conclusion:
 *
 *  - a duplicate pair cannot exist without `evidence` — the array is non-empty
 *    at the schema level, so an unexplained accusation fails validation rather
 *    than reaching a screen;
 *  - a heatmap cell carries `dispositionRule`, the sentence that placed it, and
 *    a `shortCode`, so the grid is readable without colour vision;
 *  - a comparison carries `comparability`, which is allowed to say that the two
 *    sides cannot be compared at all.
 *
 * The counterpart rule to all of the above: a population that could not be
 * assessed is reported, never dropped. Each view has a field for it — `coverage`,
 * `unplaced`, `unmatched` — because an estate screen that quietly analyses 1,412
 * of 1,760 applications and prints no note is worse than one that refuses.
 */

// ── Shared fragments ─────────────────────────────────────────────────────────

/**
 * The slice of an application these three screens need. Deliberately not
 * `ApplicationRow`: that row is the inventory grid's shape, thirty-odd fields
 * wide, and a duplicate card that depended on it would break whenever the grid
 * gained a column.
 */
export const EstateApplicationRef = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  name: z.string().min(1),
  lifecycle: Lifecycle,
  technicalHealth: TechnicalHealth,
  criticality: Criticality,
  /** Owning business domain, as the estate taxonomy names it. */
  domain: z.string().nullable(),
  vendor: z.string().nullable(),
  /** Null when finance is masked for the caller or the feed carries no figure. */
  annualRunCost: Money.nullable(),
  /** Why `annualRunCost` is null. Required whenever it is — never render a 0. */
  costUnavailableReason: z.string().nullable(),
  route: z.string().nullable(),
});
export type EstateApplicationRef = z.infer<typeof EstateApplicationRef>;

// ─────────────────────────────────────────────────────────────────────────────
// 1. DuplicateReviewView — /applications/duplicates          (UC-07-048)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What kind of observation supports an overlap claim.
 *
 * Ordered loosely by how much weight it deserves: two applications mapped to the
 * same capability by a steward is a far stronger signal than two applications
 * whose names look alike, and the UI is expected to say which it is rather than
 * merging them into one score the reader cannot interrogate.
 */
export const DuplicateEvidenceKind = z.enum([
  'SharedCapability',
  'ProcessOverlap',
  'SharedIntegration',
  'VendorOverlap',
  'NameSimilarity',
]);
export type DuplicateEvidenceKind = z.infer<typeof DuplicateEvidenceKind>;

export const DuplicateEvidence = z.object({
  kind: DuplicateEvidenceKind,
  /** One line, in the reader's language: "Both mapped to Crew Rostering (BC-114)". */
  label: z.string().min(1),
  /** The measurable behind the label, where there is one: "7 of 9 processes". */
  detail: z.string().nullable(),
  /** 0–1. The contribution this observation made to `confidenceScore`. */
  weight: z.number().min(0).max(1),
  sourceSystem: z.string().nullable(),
  observedAt: Timestamp.nullable(),
});
export type DuplicateEvidence = z.infer<typeof DuplicateEvidence>;

export const DuplicatePairStatus = z.enum(['Open', 'UnderReview', 'Confirmed', 'Dismissed']);
export type DuplicatePairStatus = z.infer<typeof DuplicatePairStatus>;

/**
 * Why a reviewer rejected an overlap claim.
 *
 * A dismissal without a reason teaches the detection rule nothing and leaves the
 * next reviewer to repeat the work, so the reason is required by the command and
 * modelled as a closed list rather than free text — free text cannot be counted,
 * and "how often is this rule wrong, and how?" is the question that decides
 * whether the rule stays switched on.
 */
export const DuplicateDismissalReason = z.enum([
  'DifferentBusinessScope',
  'DeliberateRedundancy',
  'RegionalVariant',
  'ConsolidationAlreadyPlanned',
  'EvidenceIncorrect',
]);
export type DuplicateDismissalReason = z.infer<typeof DuplicateDismissalReason>;

export const DuplicateDismissal = z.object({
  reason: DuplicateDismissalReason,
  /** Optional elaboration. The closed reason is what gets counted. */
  note: z.string().nullable(),
  actorName: z.string(),
  dismissedAt: Timestamp,
});
export type DuplicateDismissal = z.infer<typeof DuplicateDismissal>;

export const DuplicatePair = z.object({
  id: Uuid,
  left: EstateApplicationRef,
  right: EstateApplicationRef,
  /**
   * Non-empty by construction. An overlap claim with no evidence is not a weak
   * claim, it is an unsupported one, and the schema refuses to carry it.
   */
  evidence: z.array(DuplicateEvidence).min(1),
  /** 0–1. The rule's own score, shown next to the band rather than instead of it. */
  confidenceScore: z.number().min(0).max(1),
  confidence: ConfidenceBand,
  confidenceMethod: ConfidenceMethod,
  /** Capabilities both applications are mapped to. Empty is meaningful: the
   *  claim then rests on something other than capability, and the card says so. */
  sharedCapabilities: z.array(z.string()),
  /** 0–1 share of the left application's processes also covered by the right.
   *  Null when either side has no process mapping — not zero. */
  processOverlap: z.number().min(0).max(1).nullable(),
  processOverlapUnavailableReason: z.string().nullable(),
  /** Run cost of the two together, where both figures are known. */
  combinedAnnualCost: Money.nullable(),
  combinedCostStatus: MetricStatus,
  combinedCostUnavailableReason: z.string().nullable(),
  status: DuplicatePairStatus,
  dismissal: DuplicateDismissal.nullable(),
  /** The disposition already recorded against the pair, if a decision exists. */
  recordedDisposition: Disposition.nullable(),
  firstFlaggedAt: Timestamp.nullable(),
});
export type DuplicatePair = z.infer<typeof DuplicatePair>;

/** A capability with more applications against it than the estate intends. */
export const DuplicateCapabilityCluster = z.object({
  capabilityId: z.string().min(1),
  capabilityName: z.string().min(1),
  applicationCount: z.number().int().nonnegative(),
  openPairCount: z.number().int().nonnegative(),
  /** The number the operating model expects — usually 1. Null where none is set. */
  intendedApplicationCount: z.number().int().nonnegative().nullable(),
  intendedCountUnavailableReason: z.string().nullable(),
});
export type DuplicateCapabilityCluster = z.infer<typeof DuplicateCapabilityCluster>;

/**
 * Which applications the detection actually looked at.
 *
 * The single most misleading thing this screen could do is present forty pairs
 * as "the duplicates in the estate" when a fifth of the estate carries no
 * capability mapping and was never a candidate for detection.
 */
export const DuplicateCoverage = z.object({
  applicationsInScope: z.number().int().nonnegative(),
  applicationsScanned: z.number().int().nonnegative(),
  /** In scope but not scanned, with the reason stated per bucket. */
  notScanned: z.array(
    z.object({
      applicationCount: z.number().int().nonnegative(),
      reason: z.string().min(1),
    }),
  ),
  lastScanAt: Timestamp.nullable(),
  ruleSetVersion: z.string().min(1),
});
export type DuplicateCoverage = z.infer<typeof DuplicateCoverage>;

/** A dismissal reason, labelled for the dialog that collects it. */
export const DuplicateDismissalOption = z.object({
  value: DuplicateDismissalReason,
  label: z.string().min(1),
  description: z.string().min(1),
});
export type DuplicateDismissalOption = z.infer<typeof DuplicateDismissalOption>;

export const DuplicateReviewView = z.object({
  kpis: z.array(MetricValue),
  pairs: z.array(DuplicatePair),
  clusters: z.array(DuplicateCapabilityCluster),
  coverage: DuplicateCoverage,
  dismissalOptions: z.array(DuplicateDismissalOption),
});
export type DuplicateReviewView = z.infer<typeof DuplicateReviewView>;

// ─────────────────────────────────────────────────────────────────────────────
// 2. TechnologyHeatmapView — /applications/heatmap           (UC-07-049)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The modernization disposition, in the TIME vocabulary the estate uses.
 *
 * Deliberately not `Disposition` from the vocabularies: that enum records what a
 * governance body *decided* about one application (Retain, Replace, Retire…).
 * This one records where the lifecycle-and-debt model *places* a group before
 * anyone has decided anything. Collapsing the two would let a model's suggestion
 * be read as an approved decision, which is the exact confusion this screen has
 * to avoid.
 */
export const ModernizationDisposition = z.enum(['Invest', 'Tolerate', 'Migrate', 'Eliminate']);
export type ModernizationDisposition = z.infer<typeof ModernizationDisposition>;

/** Technical-debt bands. The score behind them is 0–100, higher is worse. */
export const TechnicalDebtBand = z.enum(['Low', 'Moderate', 'High', 'Severe']);
export type TechnicalDebtBand = z.infer<typeof TechnicalDebtBand>;

/**
 * The legend entry for a disposition.
 *
 * `shortCode` exists because colour may never be the only carrier of meaning:
 * roughly eight per cent of men have a colour-vision deficiency, and a
 * red/amber/green grid is unreadable to them. Every cell prints its code.
 */
export const ModernizationDispositionLegend = z.object({
  disposition: ModernizationDisposition,
  label: z.string().min(1),
  /** Two characters at most — printed inside the cell, at any width. */
  shortCode: z.string().min(1).max(2),
  description: z.string().min(1),
});
export type ModernizationDispositionLegend = z.infer<typeof ModernizationDispositionLegend>;

export const TechnologyHeatmapCell = z.object({
  id: z.string().min(1),
  lifecycle: Lifecycle,
  debtBand: TechnicalDebtBand,
  disposition: ModernizationDisposition,
  applicationCount: z.number().int().nonnegative(),
  /** Applications in the cell whose criticality is MissionCritical or
   *  BusinessCritical — a Severe/Eliminate cell full of these is the finding. */
  criticalApplicationCount: z.number().int().nonnegative(),
  annualRunCost: Money.nullable(),
  costStatus: MetricStatus,
  costUnavailableReason: z.string().nullable(),
  /** The sentence that placed this cell. Shown on hover and in the cell's detail. */
  dispositionRule: z.string().min(1),
  confidence: ConfidenceBand,
  /** A handful of named applications, so the cell can be checked rather than trusted. */
  sampleApplications: z.array(EstateApplicationRef),
});
export type TechnologyHeatmapCell = z.infer<typeof TechnologyHeatmapCell>;

/** Applications the model could not place, and why. Never folded into a cell. */
export const HeatmapUnplacedGroup = z.object({
  applicationCount: z.number().int().nonnegative(),
  reason: z.string().min(1),
  /** What would have to change for these to be placed. */
  remedy: z.string().min(1),
});
export type HeatmapUnplacedGroup = z.infer<typeof HeatmapUnplacedGroup>;

/** One line in the modernization queue beneath the grid. */
export const ModernizationCandidate = z.object({
  application: EstateApplicationRef,
  disposition: ModernizationDisposition,
  debtBand: TechnicalDebtBand,
  /** 0–100, higher is worse. Null where no assessment has been run. */
  debtScore: z.number().min(0).max(100).nullable(),
  debtScoreUnavailableReason: z.string().nullable(),
  /** End of vendor support, where the vendor publishes one. */
  supportEndsOn: IsoDate.nullable(),
  supportEndUnavailableReason: z.string().nullable(),
  driver: z.string().min(1),
  confidence: ConfidenceBand,
});
export type ModernizationCandidate = z.infer<typeof ModernizationCandidate>;

export const TechnologyHeatmapView = z.object({
  kpis: z.array(MetricValue),
  /** Axis orders are the server's, so the grid never sorts an ordinal scale
   *  alphabetically — "Active, Retired, Sunset" is not a lifecycle. */
  lifecycles: z.array(Lifecycle),
  debtBands: z.array(TechnicalDebtBand),
  cells: z.array(TechnologyHeatmapCell),
  legend: z.array(ModernizationDispositionLegend),
  unplaced: z.array(HeatmapUnplacedGroup),
  queue: z.array(ModernizationCandidate),
  modelVersion: z.string().min(1),
  assessedAt: Timestamp.nullable(),
});
export type TechnologyHeatmapView = z.infer<typeof TechnologyHeatmapView>;

// ─────────────────────────────────────────────────────────────────────────────
// 3. PortfolioComparisonView — /portfolio/compare            (UC-03-013)
// ─────────────────────────────────────────────────────────────────────────────

/** One side of a comparison: a period, or a structure held as at a date. */
export const PortfolioSnapshotRef = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  asOf: IsoDate,
  /** The taxonomy in force at `asOf`. A change here is what makes two
   *  otherwise-valid snapshots partly incomparable. */
  taxonomyVersion: z.string().min(1),
  productCount: z.number().int().nonnegative(),
  applicationCount: z.number().int().nonnegative(),
  /** Null for a live structure that was never frozen. */
  frozenAt: Timestamp.nullable(),
});
export type PortfolioSnapshotRef = z.infer<typeof PortfolioSnapshotRef>;

/**
 * How a subject differs between the two sides.
 *
 * The distinction this screen exists to protect: `Renamed` and `Moved` are not
 * `Added` plus `Removed`. A product that changed name is the same product, and a
 * comparison that reports it as one departure and one arrival invents churn that
 * never happened — then everything computed from that churn is wrong.
 */
export const PortfolioChangeKind = z.enum([
  'Added',
  'Removed',
  'Renamed',
  'Moved',
  'Merged',
  'Split',
  'Unchanged',
]);
export type PortfolioChangeKind = z.infer<typeof PortfolioChangeKind>;

export const PortfolioChangeRow = z.object({
  id: z.string().min(1),
  kind: PortfolioChangeKind,
  entityType: z.enum(['Product', 'ProductGroup', 'ValueStream', 'Application']),
  subjectId: Uuid,
  /** Names on each side. Equal unless the change is a rename. */
  baselineName: z.string().nullable(),
  comparisonName: z.string().nullable(),
  baselineParent: z.string().nullable(),
  comparisonParent: z.string().nullable(),
  /**
   * How the two sides were matched — a stable id, a steward's merge record, or a
   * name heuristic. A `Renamed` row matched only on name similarity is a guess,
   * and the row has to be able to admit that.
   */
  matchBasis: z.string().min(1),
  matchMethod: ConfidenceMethod,
  confidence: ConfidenceBand,
  /** For Merged/Split: the other participants, by name. */
  counterparts: z.array(z.string()),
  note: z.string().nullable(),
});
export type PortfolioChangeRow = z.infer<typeof PortfolioChangeRow>;

/**
 * A metric on both sides, with the delta only where a delta is meaningful.
 *
 * `comparable: false` is a first-class outcome. If the metric's definition
 * changed between the two dates, or one side predates the data, the difference
 * of the two numbers is arithmetic without meaning, and the screen must print
 * the reason where the delta would have gone.
 */
export const PortfolioMetricDelta = z.object({
  metricId: z.string().regex(/^MET-\d{3}$/),
  label: z.string().min(1),
  baseline: MetricValue,
  comparison: MetricValue,
  deltaAbsolute: z.number().nullable(),
  deltaPercent: z.number().nullable(),
  comparable: z.boolean(),
  /** Required whenever `comparable` is false. */
  incomparableReason: z.string().nullable(),
});
export type PortfolioMetricDelta = z.infer<typeof PortfolioMetricDelta>;

/** Whether the two sides may be compared at all, and where they may not. */
export const ComparabilityVerdict = z.object({
  level: z.enum(['Full', 'Partial', 'None']),
  /** One sentence, written for the reader, not the log. */
  statement: z.string().min(1),
  taxonomyChanged: z.boolean(),
  /** Named areas where a comparison is not valid, each with its reason. */
  caveats: z.array(
    z.object({
      area: z.string().min(1),
      reason: z.string().min(1),
    }),
  ),
});
export type ComparabilityVerdict = z.infer<typeof ComparabilityVerdict>;

/** Subjects present on one side that could not be matched to the other. */
export const PortfolioUnmatchedGroup = z.object({
  side: z.enum(['baseline', 'comparison']),
  count: z.number().int().nonnegative(),
  reason: z.string().min(1),
});
export type PortfolioUnmatchedGroup = z.infer<typeof PortfolioUnmatchedGroup>;

export const PortfolioComparisonView = z.object({
  baseline: PortfolioSnapshotRef,
  comparison: PortfolioSnapshotRef,
  /** Every snapshot the caller may select on either side. */
  availableSnapshots: z.array(PortfolioSnapshotRef),
  comparability: ComparabilityVerdict,
  /** Counts per change kind, from the server — never `rows.filter().length`,
   *  which would only ever count the page that happens to be loaded. */
  changeCounts: z.array(
    z.object({
      kind: PortfolioChangeKind,
      count: z.number().int().nonnegative(),
    }),
  ),
  changes: z.array(PortfolioChangeRow),
  metricDeltas: z.array(PortfolioMetricDelta),
  unmatched: z.array(PortfolioUnmatchedGroup),
});
export type PortfolioComparisonView = z.infer<typeof PortfolioComparisonView>;
